import React, {useState, useEffect} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
  Typography,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {buildingServiceCreate, roomServiceCreate} from 'src/apis/autoGenerate/services';
import {Building} from 'src/apis/autoGenerate/types';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import message from 'src/components/Message';
import {Room, RoomUpdateReq} from 'src/apis/autoGenerate/types';
import {roomServiceUpdate} from 'src/apis/autoGenerate/services';

interface AddDirectoryModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  propertyId: string;
  buildings: Building[];
  // Add these new props for edit mode
  editMode?: boolean;
  existingDirectory?: Room;
}

const steps = ['Create Building', 'Add Directory'];

// Update the getBackendErrorMessage function to handle interceptor-processed errors
const getBackendErrorMessage = (error: any, defaultMessage: string): string => {
  // Check for interceptor-processed error (from interceptors.ts)
  if (error?.message) {
    return error.message;
  }

  // Check for backend error response (if interceptors didn't process it)
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  // Check for backend message (if interceptors didn't process it)
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  // Fallback to default message
  return defaultMessage;
};

export default function AddDirectoryModal({
  open,
  onClose,
  onComplete,
  propertyId,
  buildings,
  editMode,
  existingDirectory,
}: AddDirectoryModalProps) {
  const account = useAppSelector(selectAccount);

  // Use existing building if available, otherwise we'll create one
  const needsBuilding = buildings.length === 0;

  const [activeStep, setActiveStep] = useState(needsBuilding ? 0 : 1);
  const [loading, setLoading] = useState(false);
  const [directoryName, setDirectoryName] = useState('');
  const [createdBuildingId, setCreatedBuildingId] = useState<string | null>(
    !needsBuilding && buildings[0]?.id ? buildings[0].id : null
  );
  const [createdDirectories, setCreatedDirectories] = useState<string[]>([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Initialize building ID when modal opens with existing buildings
  useEffect(() => {
    if (open && !needsBuilding && buildings[0]?.id) {
      setCreatedBuildingId(buildings[0].id);
      setActiveStep(1);
    } else if (open && needsBuilding) {
      setActiveStep(0);
      setCreatedBuildingId(null);
    }
  }, [open, needsBuilding, buildings]);

  // Update the updateDirectory function
  const updateDirectory = async () => {
    if (!existingDirectory?.id) {
      message.error('Directory ID not found');
      return;
    }

    setLoading(true);
    try {
      const updateReq: RoomUpdateReq = {
        roomId: existingDirectory.id,
        room: {
          ...existingDirectory,
          name: directoryName,
        },
      };

      await roomServiceUpdate(existingDirectory.id, updateReq);
      message.success('Directory updated successfully');
      handleClose();
      onComplete();
    } catch (error) {
      const errorMessage = getBackendErrorMessage(error, 'Failed to update directory');
      message.error(errorMessage);
      console.error('Error updating directory:', error);
    } finally {
      setLoading(false);
    }
  };

  // Modify the handleNext function to handle both create and update
  const handleNext = async () => {
    if (editMode) {
      await updateDirectory();
    } else {
      if (activeStep === 0) {
        // Step 1: Create default building if needed
        if (needsBuilding) {
          await createDefaultBuilding();
        } else {
          // Use existing building
          setCreatedBuildingId(buildings[0].id!);
          setActiveStep(1);
        }
      } else if (activeStep === 1) {
        // Step 2: Create directory
        await createDirectory();
      }
    }
  };

  // Update the createDefaultBuilding function
  const createDefaultBuilding = async () => {
    if (!account?.userDetail?.organizations?.[0]?.id) {
      message.error('Organization not found');
      return;
    }

    setLoading(true);
    try {
      const buildingData = {
        name: 'Main Building',
        description: 'Default building for commercial property',
        apartmentId: propertyId,
        orgId: account.userDetail.organizations[0].id,
      };

      const response = await buildingServiceCreate({building: buildingData});
      const buildingId = response.data.id;

      if (buildingId) {
        setCreatedBuildingId(buildingId);
        setActiveStep(1);
        message.success('Building created successfully');
      } else {
        throw new Error('No building ID returned');
      }
    } catch (error) {
      const errorMessage = getBackendErrorMessage(error, 'Failed to create building');
      message.error(errorMessage);
      console.error('Error creating building:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update the createDirectory function
  const createDirectory = async () => {
    if (!directoryName.trim()) {
      message.error('Please enter a directory name');
      return;
    }

    if (!createdBuildingId) {
      message.error('Building ID not found');
      return;
    }

    setLoading(true);
    try {
      const roomData = {
        name: directoryName,
        buildingId: createdBuildingId,
        orgId: account?.userDetail?.organizations?.[0]?.id,
      };

      await roomServiceCreate({room: roomData});

      setCreatedDirectories(prev => [...prev, directoryName]);
      setDirectoryName('');
      setShowSuccessMessage(true);

      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      const errorMessage = getBackendErrorMessage(error, 'Failed to create directory');
      message.error(errorMessage);
      console.error('Error creating directory:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAnother = async () => {
    // First, add the current directory if there's text in the input
    if (directoryName.trim()) {
      await createDirectory();
    } else {
      // If no text, just clear and reset for new entry
      setDirectoryName('');
      setShowSuccessMessage(false);
    }
  };

  const handleFinish = () => {
    handleClose();
    onComplete();
  };

  const handleClose = () => {
    setActiveStep(needsBuilding ? 0 : 1);
    setDirectoryName('');
    setCreatedBuildingId(needsBuilding ? null : buildings[0]?.id || null);
    setCreatedDirectories([]);
    setShowSuccessMessage(false);
    onClose();
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return needsBuilding ? (
          <Box>
            <Typography variant="body1" sx={{mb: 2}}>
              Creating a default building for your commercial property...
            </Typography>
            <Typography variant="body2" color="textSecondary">
              A building is required to organize directories. We'll create a "Main Building" for
              you.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body1" sx={{mb: 2}}>
              Using existing building: {buildings[0]?.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Ready to add directories to your commercial property.
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{mb: 2}}>
              Add Directory
            </Typography>

            {/* Show success message when a directory is created */}
            {showSuccessMessage && (
              <Alert severity="success" sx={{mb: 2}}>
                Directory created successfully!
              </Alert>
            )}

            {/* Show list of created directories */}
            {createdDirectories.length > 0 && (
              <Box sx={{mb: 2}}>
                <Typography variant="subtitle2" color="primary" sx={{mb: 1}}>
                  Created Directories ({createdDirectories.length}):
                </Typography>
                {/* Let DialogContent handle scrolling (avoids nested scrollbars). */}
                <Box sx={{mb: 2}}>
                  {createdDirectories.map((dir, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{
                        backgroundColor: 'action.hover',
                        padding: 0.5,
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    >
                      • {dir}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            <TextField
              fullWidth
              label="Directory Name"
              placeholder="Enter directory name (e.g., Suite 101, Office A, etc.)"
              value={directoryName}
              onChange={e => setDirectoryName(e.target.value)}
              variant="outlined"
              size="small"
              onKeyPress={e => {
                if (e.key === 'Enter' && directoryName.trim()) {
                  createDirectory();
                }
              }}
            />
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add Directory</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{maxHeight: '70vh', overflowY: 'auto'}}>
        {needsBuilding && (
          <Box sx={{mb: 3}}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}

        <Box sx={{mb: 3}}>{getStepContent(activeStep)}</Box>
      </DialogContent>

      <DialogActions sx={{px: 3, pb: 2}}>
        <Grid container spacing={2} justifyContent="flex-end" wrap="nowrap">
          <Grid item>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
          </Grid>

          {/* Show different buttons based on step and created directories */}
          {activeStep === 1 && createdDirectories.length > 0 ? (
            <>
              <Grid item>
                <Button variant="outlined" onClick={handleAddAnother} disabled={loading}>
                  Add Another
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleFinish}
                  disabled={loading}
                >
                  Finish
                </Button>
              </Grid>
            </>
          ) : (
            <Grid item>
              <Button variant="contained" onClick={handleNext} disabled={loading}>
                {loading
                  ? 'Processing...'
                  : activeStep === 0
                  ? needsBuilding
                    ? 'Create Building'
                    : 'Continue'
                  : 'Add Directory'}
              </Button>
            </Grid>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
